/**
 * TrialWidget — Banner del Dashboard mostrando días restantes del Trial 30d.
 *
 * Se autocarga desde /api/billing/status. Solo se muestra cuando
 * `subscription_status === "trialing"`. CTA principal lleva a /precios.
 *
 * Color del fondo:
 *   · > 14 días → tono dorado (relajado)
 *   · 7-14 días → tono ámbar (warm)
 *   · < 7 días  → tono rojo (urgencia)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, Clock, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

function daysBetween(now, iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const diff = end - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function pickAccent(daysLeft) {
  if (daysLeft == null) return { wrap: "border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10", text: "text-[var(--ag-gold)]" };
  if (daysLeft < 7) return { wrap: "border-red-500/50 bg-red-500/10", text: "text-red-300" };
  if (daysLeft < 15) return { wrap: "border-amber-500/40 bg-amber-500/10", text: "text-amber-300" };
  return { wrap: "border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10", text: "text-[var(--ag-gold)]" };
}

export default function TrialWidget() {
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/billing/status")
      .then(({ data }) => { if (mounted) setBilling({ ...data, _receivedAt: Date.now() }); })
      .catch(() => { /* silent */ });
    return () => { mounted = false; };
  }, []);

  if (!billing) return null;
  if (billing.subscription_status !== "trialing") return null;

  // _receivedAt se inyecta al recibir la respuesta para evitar Date.now() en render (purity).
  const daysLeft = daysBetween(billing._receivedAt, billing.trial_ends_at);
  const accent = pickAccent(daysLeft);
  const urgent = daysLeft != null && daysLeft < 7;

  return (
    <section
      className={`ag-card p-5 flex items-center gap-4 flex-wrap border ${accent.wrap}`}
      data-testid="dashboard-trial-widget"
    >
      <div className={`w-12 h-12 grid place-items-center rounded-full bg-[var(--ag-midnight-soft)] border ${accent.wrap}`}>
        {urgent
          ? <Clock size={22} weight="duotone" className={accent.text} />
          : <Sparkle size={22} weight="duotone" className={accent.text} />}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Prueba gratuita</div>
        <div className="font-display font-bold text-xl md:text-2xl tracking-tighter mt-0.5" data-testid="trial-widget-days">
          {daysLeft == null ? "Trial activo" : daysLeft === 0
            ? <span className={accent.text}>Última oportunidad · expira hoy</span>
            : daysLeft === 1
              ? <span className={accent.text}>Trial · 1 día restante</span>
              : <span className={accent.text}>Trial · {daysLeft} días restantes</span>}
        </div>
        <p className="text-xs text-[var(--ag-text-dim)] mt-1.5">
          {urgent
            ? "Activa tu plan ahora y mantén todas las funcionalidades sin interrupción."
            : "Cuando termine, conservas tus datos. Activa un plan antes para no perder el acceso al panel."}
        </p>
      </div>
      <Link
        to="/precios"
        className="ag-btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        data-testid="trial-widget-cta"
      >
        Ver planes <ArrowRight size={14} weight="bold" />
      </Link>
    </section>
  );
}
